/**
 * AK-584 — два РАЗНЫХ пустых состояния списка полей.
 *
 * Раньше было одно: строка «Полей не найдено» внутри таблицы. Формально верно,
 * по делу — тупик: новичок не понимает, что делать, а человеку с 200 полями,
 * который просто сузил фильтр, предлагать «нарисуйте поле» — выглядеть глупо.
 *
 *   no-fields  — полей нет вообще → развилка «нарисовать / загрузить файл»;
 *   no-matches — поля есть, но под фильтр не подошли → «сбросить фильтры».
 */

export interface FieldsFilterState {
  searchText: string;
  farmFilter?: string;
  clusterFilter?: string;
  brigadeFilter?: string;
  statusFilter?: string;
  cropFilter: string;
  irrigationFilter?: string;
  /**
   * Включён режим «Архив» (AK-750) — показываются только архивные поля.
   *
   * Это ТОЖЕ сужение выборки, и его обязательно учитывать: у большинства
   * тенантов архив пуст, а счётчики полей тенанта могут ещё не приехать
   * (`tenantHasFields === null`) — тогда пустой архив показывал бы новичковое
   * «Здесь появятся ваши поля» с кнопкой «нарисовать поле» человеку, у
   * которого этих полей семь с половиной тысяч.
   */
  archivedMode?: boolean;
}

export type FieldsEmptyKind = 'none' | 'no-fields' | 'no-matches';

/** Сужен ли список хоть чем-нибудь (поиск или любой из фильтров). */
export function hasActiveFilters(f: FieldsFilterState): boolean {
  return Boolean(
    f.searchText.trim() ||
      f.farmFilter ||
      f.clusterFilter ||
      f.brigadeFilter ||
      f.statusFilter ||
      f.cropFilter.trim() ||
      f.irrigationFilter !== undefined ||
      f.archivedMode,
  );
}

export function fieldsEmptyKind(args: {
  loading: boolean;
  error: string | null;
  total: number;
  filters: FieldsFilterState;
  /**
   * Есть ли поля в ТЕНАНТЕ вообще. `null` — неизвестно (счётчики не пришли).
   *
   * Список сужается не только формой: у сессии есть выбранный в шапке скоуп
   * (хозяйство/кластер), про который `hasActiveFilters` ничего не знает.
   * Клиент с 200 полями, стоящий скоупом на пустом хозяйстве, получал бы
   * «Здесь появятся ваши поля» — ревью head-web по PR #746.
   */
  tenantHasFields?: boolean | null;
}): FieldsEmptyKind {
  // Пока грузим или упали — пустого состояния нет, за них отвечает таблица.
  if (args.loading || args.error) return 'none';
  if (args.total > 0) return 'none';
  if (hasActiveFilters(args.filters)) return 'no-matches';
  // Поля в системе есть, а здесь пусто → сузил скоуп, а не «система пустая».
  if (args.tenantHasFields === true) return 'no-matches';
  return 'no-fields';
}
